import { Stack } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";

import { getEstablishmentParam } from "../../services/establishmentParam.service";
import ProcedureType from "../../components/Exams/ProcedureType/ProcedureType";
import ProcedureSearch from "../../components/Exams/ProcedureSearch/ProcedureSearch";
import { DateRange } from "../../components/UI/DateRange/DateRange";
import { IInternalProcedure } from "../../models/internal-procedure";
import useFetch from "../../hooks/useFetch";
import { IFilter } from "./ExamsPage";
import { AddIdField, IIndividualList, QPFQuickRegister } from "@4uhub/lib4uhub";
import { useTranslation } from "react-i18next";

interface IFilterProps {
  filter: IFilter;
  isDoctor: boolean;
  onFilterChange: (v: IFilter) => void;
}

const translationPath = "page.exams.";

const RANGE_MONTHS = 36;

const Filter: React.FC<IFilterProps> = ({
  filter,
  isDoctor,
  onFilterChange,
}) => {
  const { t } = useTranslation();

  const [showProcedureTypeFilter, setShowProcedureTypeFilter] = useState(false);

  const [individual, setIndividual] =
    useState<AddIdField<IIndividualList> | null>(null);

  const [procedure, setProcedure] = useState<IInternalProcedure | null>(null);

  const { sendRequest } = useFetch(getEstablishmentParam);

  const fetchEstablishmentParam = useCallback(async () => {
    const { data, success } = await sendRequest("29");
    if (data && success) {
      setShowProcedureTypeFilter(data.length > 0);
    }
  }, [sendRequest]);

  const onChangeProcedureTypeHandler = useCallback(
    (value: string) => {
      onFilterChange({
        ...filter,
        ExamType: value,
      });
    },
    [filter, onFilterChange]
  );

  const onChangeEndDateHandler = useCallback(
    (endDate: Dayjs | null) => {
      onFilterChange({ ...filter, EndDate: endDate });
    },
    [onFilterChange, filter]
  );

  const onStartDateChangeHandler = useCallback(
    (startDate: Dayjs | null) => {
      const { EndDate } = filter;

      if (startDate && EndDate) {
        const year = startDate.year().toString();

        if (startDate.isValid() && year.length === 4 && EndDate.isValid()) {
          const monthsDiff = EndDate.diff(startDate, "month", true);

          const needToAdd = monthsDiff > RANGE_MONTHS || monthsDiff < 0;

          onFilterChange({
            ...filter,
            StartDate: startDate,
            EndDate: needToAdd ? startDate?.add(RANGE_MONTHS, "month") : EndDate,
          });
        }
      } else {
        onFilterChange({ ...filter, StartDate: startDate });
      }
    },
    [filter, onFilterChange]
  );

  const onSelectedIndividual = useCallback(
    (value: AddIdField<IIndividualList> | null) => {
      setIndividual(value);
      onFilterChange({
        ...filter,
        IndividualId: value ? value.id : undefined,
        StartDate: dayjs().subtract(RANGE_MONTHS, "month"),
        EndDate: dayjs(),
      });
    },
    [filter, onFilterChange]
  );

  const onSelectedProcedure = useCallback(
    (value: IInternalProcedure | null) => {
      setProcedure(value);
      onFilterChange({
        ...filter,
        InternalProcedureCode: value?.identifier ? value.identifier : undefined,
      });
    },
    [filter, onFilterChange]
  );

  const maxEndDate = useMemo(
    () => filter.StartDate?.add(RANGE_MONTHS, "month"),
    [filter.StartDate]
  );

  useEffect(() => {
    fetchEstablishmentParam();
  }, [fetchEstablishmentParam]);

  return (
    <Stack
      pt={1}
      flexDirection={"row"}
      alignItems={isDoctor ? "start" : "center"}
      flexWrap={"wrap"}
      gap={1}
    >
      {isDoctor && (
        <Stack sx={{ minWidth: 350 }}>
          <QPFQuickRegister
            label={t(translationPath + "patient")}
            value={individual}
            disableRegister
            onChange={onSelectedIndividual}
            withBirthDate={true}
          />
        </Stack>
      )}
      <DateRange
        startDateValue={filter.StartDate}
        endDateValue={filter.EndDate}
        maxEndDate={maxEndDate}
        onChangeStartDate={onStartDateChangeHandler}
        onChangeEndDate={onChangeEndDateHandler}
        disableFuture
        clearable={false}
      />
      {showProcedureTypeFilter && (
        <ProcedureType
          procedureType={filter.ExamType}
          changeProcedureType={onChangeProcedureTypeHandler}
        />
      )}
      <ProcedureSearch value={procedure} onChange={onSelectedProcedure} />
    </Stack>
  );
};

export default memo(Filter);
